import {
  CalculateSmartAudienceAmountCommand,
  SendSmsRequestSmartAudienceCommand,
  SmartAudienceCustomersListParams,
  SmartAudienceListParams,
} from "@/models/club";
import clubRequest, { clubApi } from "../axios";
import type { SmartAudienceCalculatedAmountDto, TimeSpan } from "@/models/club";

// A smart audience sent as a discount SMS (POST api/Sms/CalculateSmartAudienceDiscountAmount and
// SendSmartAudienceDiscount): the audience fields plus the usual discount fields.
export type SmartAudienceDiscountCommand = {
  smsTemplateId?: number;
  audienceKey?: string;
  merchantUserId?: number;
  excludeCustomerIds?: number[];
  discountCode?: string;
  discountType?: number;
  amount?: number;
  percent?: number;
  maximumAmount?: number;
  minimumAmount?: number;
  count?: number;
  expireDate?: string;
  expireTime?: TimeSpan;
};

// Keys such as "rebook:nail-refill" go into the URL path; the generated client inserts them unencoded.
const pathKey = (key: string) => encodeURIComponent(key);

const getAudiences = async (params: SmartAudienceListParams) => (await clubRequest.smartAudienceList(params)).data;

const getCustomers = async ({ audienceKey, ...query }: SmartAudienceCustomersListParams) =>
  (await clubRequest.smartAudienceCustomersList({ audienceKey: pathKey(audienceKey), ...query })).data;

const getTemplates = async (audienceKey: string) =>
  (await clubRequest.smartAudienceTemplatesList({ audienceKey: pathKey(audienceKey) })).data;

const calculate = async (command: CalculateSmartAudienceAmountCommand) =>
  (await clubRequest.smsCalculateSmartAudienceAmountCreate(command)).data;

const send = async (command: SendSmsRequestSmartAudienceCommand) =>
  (await clubRequest.smsSendRequestSmartAudienceCreate(command)).data;

const calculateDiscount = async (command: SmartAudienceDiscountCommand) =>
  (await clubApi.instance.post<SmartAudienceCalculatedAmountDto>("/api/Sms/CalculateSmartAudienceDiscountAmount", command)).data;

const sendDiscount = async (command: SmartAudienceDiscountCommand) =>
  (await clubApi.instance.post("/api/Sms/SendSmartAudienceDiscount", command)).data;

// Discount SMS to every customer or to chosen ones (CalculateCustomersDiscountAmount / SendCustomersDiscount).
export type CustomersDiscountCommand = Omit<SmartAudienceDiscountCommand, "audienceKey" | "merchantUserId" | "excludeCustomerIds"> & { allCustomers: boolean; customerIds?: number[] };
const calculateCustomersDiscount = async (command: CustomersDiscountCommand) =>
  (await clubApi.instance.post<SmartAudienceCalculatedAmountDto>("/api/Sms/CalculateCustomersDiscountAmount", command)).data;
const sendCustomersDiscount = async (command: CustomersDiscountCommand) =>
  (await clubApi.instance.post("/api/Sms/SendCustomersDiscount", command)).data;

const smartAudienceService = { getAudiences, getCustomers, getTemplates, calculate, send, calculateDiscount, sendDiscount, calculateCustomersDiscount, sendCustomersDiscount };

export default smartAudienceService;
